// Per-school settings -- iteration 144
// Unlike SystemSetting (global), these are scoped to one school. Values are
// stored as strings and parsed at the read site. Absence of a stored row
// means "use the key's default" (SCHOOL_SETTING_DEFAULTS).

/** Known per-school setting keys. Add new keys here as policies emerge. */
export const SCHOOL_SETTING_KEYS = {
  /** Whether coaches (not also admins) may create/import players at this school. */
  coachesCanAddPlayers: "players.coaches_can_add",
  /**
   * 162 -- whether any coach at this school may view/edit ANY team in the school
   * (not just their CoachTeam assignments). Default-on for administrative
   * continuity; a school can opt back to strict-assignment by setting "false".
   */
  coachesCrossTeamAccess: "teams.coaches_cross_team_access",
} as const;

export type SchoolSettingKey =
  (typeof SCHOOL_SETTING_KEYS)[keyof typeof SCHOOL_SETTING_KEYS];

/** All known keys as a flat array (for validation / iteration). */
export const SCHOOL_SETTING_KEY_LIST: SchoolSettingKey[] = Object.values(
  SCHOOL_SETTING_KEYS,
);

/**
 * Default string value for each key, used when no row exists for the school.
 * Boolean flags default-on are stored as "true".
 */
export const SCHOOL_SETTING_DEFAULTS: Record<SchoolSettingKey, string> = {
  [SCHOOL_SETTING_KEYS.coachesCanAddPlayers]: "true",
  [SCHOOL_SETTING_KEYS.coachesCrossTeamAccess]: "true",
};

/** Parse a stored string value as a boolean ("true" => true, anything else => false). */
export function schoolSettingBool(value: string | null | undefined, fallback: boolean): boolean {
  if (value === null || value === undefined || value === "") return fallback;
  return value === "true";
}

export interface SchoolSettingPublic {
  school_id: string;
  key: SchoolSettingKey;
  value: string;
  updated_at: string;
}

/** GET /schools/:id/settings response -- effective settings (stored merged over defaults). */
export interface SchoolSettingsResponse {
  school_id: string;
  settings: SchoolSettingPublic[];
}
